import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { sendQuizFunnelEvent } from "../../../lib/pageviewTracker";

export const CTA_LABEL = "Start My Free 60-Day Plan";
export const OFFER_LINE =
  "Free for 60 days. No card and no automatic charge. On day 60, choose $10/month, $120/year, or $300 lifetime access — or stop.";

/** Signup link that carries email + persona + source and lands on the Habit Loop. */
export function registerHref({ slug, leadEmail, placement }) {
  const q = new URLSearchParams({
    persona: slug || "",
    ref: "quiz-result",
    utm_source: "quiz",
    utm_medium: "onsite_quiz",
    utm_campaign: "result",
    utm_content: placement,
    next: "/app/habits",
  });
  if (leadEmail) q.set("email", leadEmail);
  return `/register?${q.toString()}`;
}

/** The four-step bridge shown above the preview CTA only. */
const Bridge = ({ shortName }) => (
  <div
    className="mx-auto max-w-2xl text-left bg-white border border-[#E6E3DB] p-6 sm:p-8"
    data-testid="result-cta-bridge"
  >
    <p className="eyebrow text-[#C05C3D]">What happens when you start</p>
    <ol className="mt-4 space-y-3">
      {[
        <>Create your free account — no card, nothing charged.</>,
        <>Your <strong>{shortName}</strong> starter habit is switched on and your result is saved to it.</>,
        <>Your Spending Plan opens — add your bills and paydays to see your Running Balance.</>,
        <>See the plans and pricing before any payment: <strong>$10/month, $120/year, or $300 lifetime access</strong>. No payment decision until day 60.</>,
      ].map((step, i) => (
        <li key={i} className="flex items-start gap-3" data-testid={`result-cta-bridge-step-${i + 1}`}>
          <span className="shrink-0 mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#2C302E] text-white text-xs font-semibold">
            {i + 1}
          </span>
          <span className="text-[#2C302E] leading-relaxed">{step}</span>
        </li>
      ))}
    </ol>
    <p className="mt-5 text-sm text-[#4d4f4c] leading-relaxed" data-testid="result-cta-bridge-closing">
      Your free 60 days begin when you create your account. You&apos;ll get the full Spending Plan,
      Running Balance, and persona-matched Habit Loop from day one. If you step away, your saved
      result and progress stay available when you return.
    </p>
  </div>
);

/**
 * One of three tracked signup placements on the result page: top, preview, bottom.
 * Same label and offer line everywhere; only `utm_content` + the funnel step differ.
 * `bridge` adds the four-step "what happens when you start" block (preview only).
 */
export const ResultCta = ({ slug, leadEmail, placement, isMember, compact = false, bridge = false, lede, shortName }) => {
  const track = () => sendQuizFunnelEvent(`cta_${placement}`, { persona: slug });

  if (isMember) {
    return (
      <section className={`${compact ? "mt-8" : "mt-12"} text-center`} data-testid={`result-cta-${placement}`}>
        <Link
          to="/app/habits"
          className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#C05C3D] text-white rounded-sm hover:bg-[#a34a2e] transition-colors text-base font-semibold"
          data-testid={`result-cta-${placement}-member`}
        >
          Open habit loop <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    );
  }

  const button = (
    <Link
      to={registerHref({ slug, leadEmail, placement })}
      onClick={track}
      className={`inline-flex w-full sm:w-auto sm:shrink-0 items-center justify-center gap-2 py-3.5 bg-[#C05C3D] text-white rounded-sm hover:bg-[#a34a2e] transition-colors font-semibold text-center ${
        compact ? "px-4 sm:px-7 text-[15px] sm:text-base" : "px-6 sm:px-7 text-base"
      }`}
      data-testid={`result-cta-${placement}-button`}
    >
      {CTA_LABEL} <ArrowRight className={`h-4 w-4 shrink-0 ${compact ? "hidden sm:inline" : ""}`} />
    </Link>
  );

  if (compact) {
    return (
      <section
        className="mt-8 bg-white border border-[#E6E3DB] p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        data-testid={`result-cta-${placement}`}
      >
        <div>
          {lede && <p className="font-semibold text-[#2C302E]">{lede}</p>}
          <p className="mt-1 text-xs text-[#787671] leading-relaxed" data-testid={`result-cta-${placement}-offer`}>
            {OFFER_LINE}
          </p>
        </div>
        {button}
      </section>
    );
  }

  return (
    <section className="mt-12 text-center" data-testid={`result-cta-${placement}`}>
      {lede && (
        <p className="text-lg sm:text-xl text-[#2C302E] leading-relaxed max-w-2xl mx-auto">{lede}</p>
      )}
      {bridge && (
        <div className="mt-6">
          <Bridge shortName={shortName} />
        </div>
      )}
      <div className="mt-6">{button}</div>
      <p className="mt-3 text-xs text-[#787671] leading-relaxed max-w-md mx-auto" data-testid={`result-cta-${placement}-offer`}>
        {OFFER_LINE}
      </p>
    </section>
  );
};
